import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Group, Stack, Text, Title, Select } from '@mantine/core';
import { IconBed, IconChevronUp, IconToolsKitchen } from '@tabler/icons-react';
import { inject, observer } from 'mobx-react';
import { getNearestTime, useSimulation } from '../../../global/hooks/useSimulation.tsx';
import { Area, AreaChart, CartesianGrid, ReferenceArea, ReferenceDot, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useElementSize } from '@mantine/hooks';
import { t } from 'i18next';
import i18next from 'i18next';
import { useParams } from 'react-router';
import { useGetLabTests } from '../../../global/entities/LabTest/queries/useGetLabTests.tsx';
import { PatientStore } from '../../../global/Store/PatientStore.ts';

// Define the correct data structure returned by transformCurvesToData
interface ChartDataPoint {
    time: string;
    curve1: number | null;
    curve2: number | null;
    curve3: number | null;
    curve4: number | null;
    curve5: number | null;
    curve6: number | null;
    curve7: number | null;
    curve8: number | null;
    curve9: number | null;
}

// Interface for lab test data
interface LabTestDataPoint {
    time: string;
    value: number;
    description: string;
}

interface GraphSimulationProps {
    patientStore?: PatientStore;
}

// Custom ReferenceDot component with hover functionality
interface CustomReferenceDotProps {
    cx: number;
    cy: number;
    payload: {
        time: string;
        value: number;
        description: string;
    };
}

const CustomReferenceDot: React.FC<CustomReferenceDotProps> = ({ cx, cy, payload }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <g>
            <circle
                cx={cx}
                cy={cy}
                r={4}
                fill="red"
                stroke="darkred"
                strokeWidth={2}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            />
            {isHovered && (
                <g>
                    {/* Tooltip background */}
                    <rect
                        x={cx + 10}
                        y={cy - 20}
                        width={40}
                        height={25}
                        fill="white"
                        stroke="gray"
                        strokeWidth={1}
                        rx={4}
                        style={{ 
                            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))',
                            pointerEvents: 'none'
                        }}
                    />
                    {/* Tooltip text - only value */}
                    <text
                        x={cx + 30}
                        y={cy - 5}
                        fill="black"
                        fontSize="14"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ pointerEvents: 'none' }}
                    >
                        {payload.value}
                    </text>
                </g>
            )}
        </g>
    );
};

const GraphSimulation: React.FC<GraphSimulationProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const { simulationCurveGraph, transformCurvesToData, activities, therapeuticParams, advancedSettings } =
            useSimulation();

        const params = useParams<{ tabId: string; patientId: string }>();
        const [data, setData] = useState<ChartDataPoint[] | null>(null);
        const { ref } = useElementSize();
        
        // PK selection state
        const [selectedPK, setSelectedPK] = useState<string | null>(null);
        const [selectedDate, setSelectedDate] = useState<string | null>(null);

        // Get lab tests data
        const { data: labTests, refetch: refetchLabTests } = useGetLabTests(patientStore?.patientId);

        // Get current language for date formatting
        const currentLanguage = i18next.language || 'en';

        // Create PK options from unique lab test descriptions
        const pkOptions = useMemo(() => {
            if (!labTests || labTests.length === 0) return [];
            
            const uniqueDescriptions = new Map();
            labTests.forEach(labTest => {
                if (!uniqueDescriptions.has(labTest.description)) {
                    uniqueDescriptions.set(labTest.description, {
                        value: `${labTest.biomarkerId}`,
                        label: labTest.description
                    });
                }
            });
            
            return Array.from(uniqueDescriptions.values());
        }, [labTests, currentLanguage]);

        // Reload lab tests when language changes
        useEffect(() => {
            if (patientStore?.patientId) 
                refetchLabTests();
            
        }, [currentLanguage, refetchLabTests, patientStore?.patientId]);

        // Get date options based on selected PK (biomarker)
        const getDateOptions = (pkValue: string) => {
            if (!labTests || !pkValue) return [];
            
            // Extract biomarker ID from the selected PK value
            const biomarkerId = parseInt(pkValue);
            
            // Filter lab tests by biomarker ID and get unique dates
            const filteredTests = labTests.filter(test => test.biomarkerId === biomarkerId);
            const uniqueDates = new Map();
            
            filteredTests.forEach(test => {
                const dateKey = test.dateMeasured.toISOString().split('T')[0];
                if (!uniqueDates.has(dateKey)) {
                    // Format date according to current language
                    let formattedDate: string;
                    try {
                        formattedDate = test.dateMeasured.toLocaleDateString(currentLanguage, { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        });
                    } catch {
                        // Fallback to browser default if locale isn't supported
                        formattedDate = test.dateMeasured.toLocaleDateString(undefined, { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                        });
                    }
                    
                    uniqueDates.set(dateKey, {
                        value: dateKey,
                        label: formattedDate
                    });
                }
            });
            
            return Array.from(uniqueDates.values()).sort((a, b) => 
                new Date(b.value).getTime() - new Date(a.value).getTime()
            );
        };

        // Prepare lab test data for the selected PK and date
        const labTestData: LabTestDataPoint[] = useMemo(() => {
            if (!labTests || !selectedPK || !selectedDate) return [];
            
            // Extract biomarker ID from the selected PK value
            const biomarkerId = parseInt(selectedPK);
            
            // Filter lab tests by biomarker ID and selected date
            const filteredTests = labTests.filter(test => {
                const testDate = test.dateMeasured.toISOString().split('T')[0];
                return test.biomarkerId === biomarkerId && testDate === selectedDate;
            });
            
            // Convert to LabTestDataPoint format
            const result = filteredTests.map(test => {
                // Extract time without timezone conversion
                // Get the original database time by using the ISO string and extracting the time part
                const isoString = test.dateMeasured.toISOString(); // This gives us the original UTC time
                const timePart = isoString.split('T')[1].substring(0, 5); // Extract HH:MM from ISO string
                
                return {
                    time: timePart, // Use the original database time
                    value: test.value,
                    description: test.description
                };
            }).sort((a, b) => a.time.localeCompare(b.time)); // Sort by time
            
            return result;
        }, [labTests, selectedPK, selectedDate]);

        // Reset date selection when PK changes
        useEffect(() => {
            setSelectedDate(null);
        }, [selectedPK]);

        useEffect(() => {
            if (!simulationCurveGraph?.curve1.length) 
                return undefined;

            const curve: ChartDataPoint[] = transformCurvesToData(simulationCurveGraph);
            setData(curve);
        }, [simulationCurveGraph]);

        const startTime = useMemo(() => {
            if (!simulationCurveGraph) 
                return;
            
            const [hour, minute, second] = simulationCurveGraph!.xAxisStart.split(':').map(Number);
            return { hour, minute, second };
        }, [simulationCurveGraph]);

        const ticks = useMemo(() => {
            if (!startTime) 
                return;
            
            const ticks = [];
            for (let i = 0; i < 24; i += 2)
                {ticks.push(
                    `${String(startTime.hour + i >= 24 ? startTime.hour + i - 24 : startTime.hour + i).padStart(2, '0')}:00`,
                );}

            return ticks;
        }, [startTime]);

        const lunchTimeData = useMemo(() => {
            const startStr = `${startTime?.hour}:${startTime?.minute}`;
            const time = activities?.lunchTime;
            if (!time || !startStr) 
                return { hidden: true, left: '0%' };
            
            return {
                hidden: isHidden(time, startStr),
                left: `${calculatePercentDay(time, startStr)}%`,
            };
        }, [activities?.lunchTime, startTime?.hour, startTime?.minute]);

        const sleepTimeData = useMemo(() => {
            const startStr = `${startTime?.hour}:${startTime?.minute}`;
            const time = activities?.sleepTime;
            if (!time || !startStr) 
                return { hidden: true, left: '0%' };
            
            return {
                hidden: isHidden(time, startStr),
                left: `${calculatePercentDay(time, startStr)}%`,
            };
        }, [activities?.sleepTime, startTime?.hour, startTime?.minute]);

        const referenceAreas = useMemo(() => {
            if (!activities || !therapeuticParams || !data) 
                return [];
            
            return [
                {
                    start: activities?.period1Start,
                    end: activities?.period1End,
                    label: `${t('graphSimulation.firstWindow')}: ${Math.round(simulationCurveGraph?.period1Percentage || 0)}`,
                    color:
                        (advancedSettings?.threshold || 0) > Math.round(simulationCurveGraph?.period1Percentage || 0)
                            ? 'red'
                            : 'green',
                },
                {
                    start: activities?.period2Start,
                    end: activities?.period2End,
                    label: `${t('graphSimulation.secondWindow')}: ${Math.round(simulationCurveGraph?.period2Percentage || 0)}`,
                    color:
                        (advancedSettings?.threshold || 0) > Math.round(simulationCurveGraph?.period2Percentage || 0)
                            ? 'red'
                            : 'green',
                },
            ].map((ref, index) => {
                if (!ref.start || !ref.end) return null;
                
                const nearestStart = getNearestTime(ref.start, data);
                const nearestEnd = getNearestTime(ref.end, data);
                if (!nearestStart || !nearestEnd) return null;
                
                return (
                    <ReferenceArea
                        key={index}
                        x1={data.findIndex((el) => el.time === nearestStart)}
                        x2={data.findIndex((el) => el.time === nearestEnd)}
                        stroke={ref.color}
                        strokeOpacity={0.75}
                        fill={ref.color}
                        fillOpacity={0.1}
                        isFront
                        label={`${ref.label}%`}
                    />
                );
            }).filter(Boolean);
        }, [activities, therapeuticParams, data]);

        // Helper function to transform lab test to chart dot
        const createLabTestDot = (labTest: LabTestDataPoint, chartData: ChartDataPoint[]) => {
            const nearestTime = getNearestTime(labTest.time, chartData);
            if (!nearestTime) return null;
            
            const dataIndex = chartData.findIndex(el => el.time === nearestTime);
            return dataIndex !== -1 ? {
                x: dataIndex,
                y: labTest.value,
                time: labTest.time,
                description: labTest.description
            } : null;
        };

        // Use real lab test data instead of mock dots
        const labTestDots = useMemo(() => {
            if (!data?.length || !labTestData?.length) return [];
            
            return labTestData.reduce<Array<{x: number, y: number, time: string, description: string}>>((dots, labTest) => {
                const dot = createLabTestDot(labTest, data);
                return dot ? [...dots, dot] : dots;
            }, []);
        }, [data, labTestData]);

        return simulationCurveGraph && data ? (
            <Stack gap={0} pos={'relative'}>
                <Flex justify={'space-between'} mb={'md'}>
                    <Title order={4}>{t('Simulation')}</Title>
                </Flex>
                
                <Box w="100%" pl={'lg'} mb={'l'}>
                    <Flex gap="md" align="flex-start" w="100%">
                        <Title order={6} style={{ minWidth: '50px', marginTop: '8px' }}>
                            {t('table.dosageSimulation.pk')}
                        </Title>
                        
                        <Select
                            placeholder={t('table.dosageSimulation.selectPK')}
                            data={pkOptions}
                            value={selectedPK}
                            onChange={setSelectedPK}
                            clearable
                            searchable
                        />
                        
                        {selectedPK && (
                            <Select
                                placeholder={t('table.dosageSimulation.selectDate')}
                                data={getDateOptions(selectedPK)}
                                value={selectedDate}
                                onChange={setSelectedDate}
                                clearable
                            />
                        )}
                    </Flex>
                </Box>
                
                <ResponsiveContainer ref={ref} width="100%" height={params.tabId === 'hcpMedicalOverview' ? 200 : 400}>
                    <AreaChart data={data} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                        <XAxis height={20} dataKey="time" ticks={ticks} />
                        <YAxis width={40} />
                        <CartesianGrid strokeDasharray="3 3" />
                        {/*<Tooltip content={<CustomTooltip />} />*/}

                        {curves.map(({ key, fill, stroke = 'none', strokeWidth = 0 }, index) => {
                            return (
                                <Area
                                    key={key ?? index}
                                    type="natural"
                                    dataKey={key}
                                    stroke={stroke}
                                    strokeWidth={strokeWidth}
                                    fillOpacity={1}
                                    fill={fill}
                                />
                            );
                        })}
                        {referenceAreas}
                        
                        {labTestDots.map((dot, index) => (
                            <ReferenceDot
                                key={`lab-test-dot-${index}`}
                                x={dot.x}
                                y={dot.y}
                                r={4}
                                fill="red"
                                stroke="darkred"
                                strokeWidth={2}
                                shape={(props: any) => (
                                    <CustomReferenceDot
                                        cx={props.cx}
                                        cy={props.cy}
                                        payload={{
                                            time: dot.time,
                                            value: dot.y,
                                            description: dot.description
                                        }}
                                    />
                                )}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
                <Box
                    pos="absolute"
                    top={115}
                    right={0}
                    p="xs"
                    bg="#f7f7c9"
                    style={{
                        borderRadius: 5,
                        boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
                        zIndex: 10,
                    }}
                >
                    <Text size="xs">
                        {`${t('toolTip.scoreTotal')}: ${Math.round(simulationCurveGraph?.totalScore || 0)}%`}
                    </Text>
                    <Text size="xs">
                        {`${t('toolTip.effetMontagneRusse')}: ${simulationCurveGraph?.rollerCoasterEffect || 0}%`}
                    </Text>
                    <Text size="xs">{`${t('toolTip.ngMlLunch')}: ${simulationCurveGraph?.ngMlLunchTime || '-'}`}</Text>
                    <Text size="xs">{`${t('toolTip.ngMlSleep')}: ${simulationCurveGraph?.ngMlSleepTime || '-'}`}</Text>
                </Box>
                <Group w={'calc(100% - 80px)'} style={{ position: 'relative', minHeight: 80, marginLeft: 24 }}>
                    <Stack
                        hidden={lunchTimeData.hidden}
                        gap={0}
                        align="center"
                        style={{ position: 'absolute', left: lunchTimeData.left }}
                    >
                        <IconChevronUp size={18} color="gray" />
                        <IconToolsKitchen size={18} color="gray" />
                        <Text c="dimmed" size="xs">
                            {t('graphSimulation.lunch')}
                        </Text>
                    </Stack>

                    <Stack
                        hidden={sleepTimeData.hidden}
                        gap={0}
                        align="center"
                        style={{ position: 'absolute', left: sleepTimeData.left }}
                    >
                        <IconChevronUp size={18} color="gray" />
                        <IconBed size={18} color="gray" />
                        <Text c="dimmed" size="xs">
                            {t('graphSimulation.sleep')}
                        </Text>
                    </Stack>
                </Group>
            </Stack>
        ) : null;
    }),
);

export default GraphSimulation;

const calculatePercentDay = (time: string, startTime: string) => {
    if (!time || !startTime) return 0;

    const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
    };

    const startMinutes = toMinutes(startTime);
    const timeMinutes = toMinutes(time);

    // Calcule la différence en minutes avec wrap-around si nécessaire
    let diffMinutes = timeMinutes - startMinutes;
    if (diffMinutes < 0) 
        diffMinutes += 1440;

    // Adapte à une journée de 22h (1320 minutes)
    if (diffMinutes > 1320) diffMinutes = 1320; // Clamp to max range

    return (diffMinutes / 1320) * 100;
};

const isHidden = (time: string, startTime: string): boolean => {
    if (!time || !startTime) return false;

    const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
    };

    const startMinutes = toMinutes(startTime);
    let lowerBound = startMinutes - 60; // startTime - 2h

    // Gérer le wrap-around minuit (si < 0, ajoute 1440 minutes)
    if (lowerBound < 0) 
        lowerBound += 1440;

    const timeMinutes = toMinutes(time);

    // Vérifie si `time` est dans l'intervalle [lowerBound, startMinutes]
    if (formatTime(time) === formatTime(startTime)) return false;

    if (lowerBound < startMinutes) 
        return timeMinutes >= lowerBound && timeMinutes <= startMinutes;
     else {
        // Cas où la plage chevauche minuit (ex: startTime = 01:00, plage = [23:00, 01:00])
        return timeMinutes >= lowerBound || timeMinutes <= startMinutes;
    }
};

const formatTime = (time: string): string => {
    const [h, m] = time.split(':').map(Number);
    const paddedH = h.toString().padStart(2, '0');
    const paddedM = m.toString().padStart(2, '0');
    return `${paddedH}:${paddedM}:00`;
};

const curves = [
    { key: 'curve9', fill: '#dce9f2' },
    { key: 'curve8', fill: '#cbe2f3' },
    { key: 'curve7', fill: '#a7ceeb' },
    { key: 'curve6', fill: '#8ec0e5' },
    { key: 'curve5', fill: '#7cb6e1', stroke: '#4d92ce', strokeWidth: 4 },
    { key: 'curve4', fill: '#7cb6e1' },
    { key: 'curve3', fill: '#8ec0e5' },
    { key: 'curve2', fill: '#a7ceeb' },
    { key: 'curve1', fill: '#ffffff', stroke: '#cbe2f3', strokeWidth: 1 },
];
